import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useState,
} from 'react'

import { useMutation } from '@tanstack/react-query'

import { IdCard } from 'lucide-react'

import skipPreCheckMutation from './services/skip-pre-check'
import VerificationComponent from './Components/VerificationComponent'
import SkipConfirmDialog from './Components/SkipConfirmDialog'
import PhysicalDocumentNotice from './Components/PhysicalDocumentNotice'
import TruCheckCardPhotoNotice from './Components/TruCheckCardPhotoNotice'
import Overlay from '../../../../Components/Overlay'

const PreCheck = forwardRef(({
    data,
    onSubmitted,
    onSkipped,
    documentText,
    handleNext,
    formKey,
    refetch,
    documentId,
    optional,
    skipLabel,
    isFirstVerification,
}, ref) => {

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => ({
            isValid: true,
            value: data,
        }),
    }))

    const isAlreadySubmitted = data?.status === 'submitted'

    // Every page opens on an intro screen showing the physical-document rules
    // (and, for optional pages, a Skip option) so the applicant sees the critical
    // "no screen photos / originals only" guidance before the Veriff frame — which
    // goes full-screen on mobile — is mounted. Pages whose session already exists,
    // or that are already submitted, go straight to the verification flow.
    const [started, setStarted] = useState(
        !!data?.sessionUrl || isAlreadySubmitted
    )

    const handleStart = useCallback(() => setStarted(true), [])

    // An on-computer verification was canceled: return to this page's intro screen (the
    // physical-document notice + Start button) so the applicant can begin again. The
    // Veriff session URL is unchanged, so re-entering rebuilds the frame for it.
    const handleBackToIntro = useCallback(() => setStarted(false), [])

    // Skipping is a last resort, so confirm the applicant's intent first.
    const [showSkipConfirm, setShowSkipConfirm] = useState(false)

    const {
        isPending: skipPreCheckIsPending,
        mutate: skipPreCheck,
    } = useMutation({
        mutationFn: skipPreCheckMutation,
        onSuccess: () => {
            // Skip recorded on the backend. Let the parent advance to the next
            // page (or submit the form if this was the last page).
            if (onSkipped) {
                onSkipped()
            }
        },
    })

    const handleSkip = useCallback(() => setShowSkipConfirm(true), [])

    const handleConfirmSkip = useCallback(() => {
        skipPreCheck({
            key: formKey,
            currentPageType: "pre_check",
            currentPageDocumentId: documentId,
        })
    }, [skipPreCheck, formKey, documentId])

    // Page that hasn't been started yet: show the intro screen with the notice.
    const showIntro = !started && !isAlreadySubmitted

    if (!showIntro) {
        return (
            <VerificationComponent
                data={data}
                onSubmitted={onSubmitted}
                onCanceled={handleBackToIntro}
                documentText={documentText}
                handleNext={handleNext}
                formKey={formKey}
                refetch={refetch}
                documentId={documentId}
            />
        )
    }

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Identity Verification</h1>
                    {documentText && (
                        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg">
                            <IdCard className="w-4 h-4 shrink-0" />
                            {documentText}
                        </div>
                    )}
                    <p className="text-gray-600 text-base">
                        Verifying your identity is the fastest and most reliable way for us to
                        confirm your details.{optional && <> Please complete it whenever possible. Only choose
                        “{skipLabel || 'Skip'}” if you genuinely do not have a <strong className="font-semibold text-gray-900">non-expired</strong> US ID card to provide.</>}
                    </p>
                </div>

                <PhysicalDocumentNotice />

                {/* First verification only: the live selfie captured here becomes the photo
                    on the candidate's TruCheck Card. */}
                {isFirstVerification && <TruCheckCardPhotoNotice />}

                <div className="flex flex-col items-center justify-center pt-4 pb-6 gap-8 text-center">
                    {optional ? (
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 w-full sm:w-auto">
                            <button
                                onClick={handleStart}
                                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all"
                            >
                                Start Verification
                            </button>
                            <span className="text-gray-400 font-semibold text-sm uppercase tracking-wide">
                                or
                            </span>
                            <button
                                onClick={handleSkip}
                                disabled={skipPreCheckIsPending}
                                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {skipLabel || 'Skip'}
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleStart}
                            className="w-full sm:w-auto px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all"
                        >
                            Start Verification
                        </button>
                    )}
                </div>
            </div>

            {showSkipConfirm && (
                <SkipConfirmDialog
                    confirmLabel={skipLabel}
                    isPending={skipPreCheckIsPending}
                    onConfirm={handleConfirmSkip}
                    onCancel={() => setShowSkipConfirm(false)}
                />
            )}

            {skipPreCheckIsPending && <Overlay />}
        </div>
    )

})

export default PreCheck
